"use client";

import React, { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import MeasurementProtocol from "./measurement-protocol";

interface ConditionalMeasurementProtocolProps {
  flagKey: string;
  flagType?: "boolean" | "variant" | "batch";
  additionalContext?: Record<string, any>;
  entityId?: string;
  namespaceKey?: string;
  measurementId: string;
  abTestHeader: any;
}

const ConditionalMeasurementProtocol = ({ 
  flagKey,
  flagType = "boolean",
  additionalContext = {},
  entityId,
  namespaceKey = "ColletteWeb",
  measurementId,
  abTestHeader
}: ConditionalMeasurementProtocolProps) => {
  const pathname = usePathname();
  const [fullUrl, setFullUrl] = useState("");
  const [shouldTrack, setShouldTrack] = useState<boolean>(false);
  const [isEvaluated, setIsEvaluated] = useState<boolean>(false);
  const evaluationRef = useRef<boolean>(false);

  useEffect(() => {
    const evaluateTrackingFlag = async () => {
      // Prevent duplicate evaluations
      if (evaluationRef.current) return;
      evaluationRef.current = true;

      try {
        console.log("=== Evaluating Flipt Flag for GA4 Tracking ===");
        console.log("Flag Key:", flagKey);
        console.log("Current pathname:", pathname?.toLowerCase());
        console.log("Additional context:", additionalContext);

        // Build context with URL information
        const context = {
          pageUrl: pathname?.toLowerCase(),
          ...additionalContext
        };

        console.log("Context being sent to Flipt:", context);

        // Call the Flipt API
        const response = await fetch("/api/feature-toggle/evaluate", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            flagKey,
            flagType,
            context,
            entityId,
            namespaceKey
          })
        });

        if (!response.ok) {
          console.error("Flipt evaluation failed:", response.status, response.statusText);
          // Default to false on error
          setShouldTrack(false);
          setIsEvaluated(true);
          return;
        }

        const result = await response.json();
        console.log("Flipt evaluation result:", result);

        // Handle different flag types
        let trackingEnabled = false;
        
        if (flagType === "boolean") {
          trackingEnabled = result.enabled || false;
        } else if (flagType === "variant") {
          // For variant flags, you might want to check for specific variant keys
          trackingEnabled = result.variant?.key === "enabled" || result.variant?.key === "track";
        } else if (flagType === "batch") {
          // For batch flags, check if any response indicates tracking should be enabled
          trackingEnabled = result.responses?.some((resp: any) => 
            resp.type === "BOOLEAN_EVALUATION_RESPONSE_TYPE" && resp.boolean?.enabled
          );
        }

        console.log("Tracking enabled:", trackingEnabled);
        setShouldTrack(trackingEnabled);
        setIsEvaluated(true);
        setFullUrl(window.location.href);
      } catch (error) {
        console.error("Error evaluating Flipt flag:", error);
        // Default to false on error
        setShouldTrack(false);
        setIsEvaluated(true);
      }
    };

    evaluateTrackingFlag();
  }, [pathname, flagKey, flagType, additionalContext, entityId, namespaceKey]);

  // Only render MeasurementProtocol if flag evaluation is complete and tracking is enabled
  if (!isEvaluated) {
    console.log("Waiting for Flipt flag evaluation...");
    return null;
  }

  if (!shouldTrack) {
    console.log("Flipt flag disabled tracking for this page");
    return null;
  }

  console.log("Flipt flag enabled - proceeding with GA4 tracking");
  
  // Extract specific values from abTestHeader
  const getHeaderValue = (key: string) => {
    if (!abTestHeader) {
      return "";
    }

      // Type guard: ensure abTestHeader is a string before calling split
    if (typeof abTestHeader !== "string") {
      console.warn("abTestHeader is not a string:", typeof abTestHeader, abTestHeader);
      return "";
    }
    
    const pairs = abTestHeader.split(",");
    const result = pairs
      .find(pair => {
        const [pairKey] = pair.split("=");
        return pairKey.trim() === key;
      })
      ?.split("=")[1]
      ?.trim();

    return result || "";
  };

    
  // get the test_name
  const testName = getHeaderValue("test_name");

  // get the bucket
  const bucket = getHeaderValue("bucket");

  // get the user tuid
  const userTuid = getHeaderValue("user");

    
  console.log("AB Test Header User Value:", {
    userTuid,
    testName,
    bucket,
    fullHeader: abTestHeader
  });
  
  // put together the data
   const ga4EventData = {
    events: [
      {
        name: "experiment_trigger",
        params: {
          experiment_id: testName || "",
          variant: bucket || "",
          tuid:userTuid || "",
          timestamp: new Date().toISOString(),
          page_location: fullUrl || ""
        }
      }
    ]
  };
  return <MeasurementProtocol data={ga4EventData} measurementId={measurementId} />;
};

export default ConditionalMeasurementProtocol;
